import { useMutation, useQueryClient } from 'react-query';
import { api } from '../api/interceptor';

type CreateFnTypes = {
  url: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  headers?: any;
  method: 'post' | 'patch' | 'delete';
};

/**
 * Controls what gets invalidated after a successful mutation.
 *   - `true`  (default): invalidate every query in the cache. Kept as the
 *               default so existing callers behave exactly as before.
 *   - `false`: invalidate nothing — use this when the caller refetches the
 *               affected queries itself (avoids an app-wide refetch storm).
 *   - `string[]`: invalidate only the queries whose key starts with one of the
 *               provided prefixes.
 */
type InvalidateOption = boolean | string[];

interface MutateOptions {
  invalidate?: InvalidateOption;
}

export const useMutateQuery = ({ invalidate = true }: MutateOptions = {}) => {
  const queryClient = useQueryClient();

  const createElement = async ({
    url,
    body,
    method,
    headers,
  }: CreateFnTypes) => {
    const { data } = await api[method](url, body, {
      headers: headers,
    });
    return data;
  };

  return useMutation(createElement, {
    onSuccess: () => {
      if (invalidate === false) return;
      if (Array.isArray(invalidate)) {
        invalidate.forEach((prefix) =>
          queryClient.invalidateQueries({
            predicate: (query) =>
              typeof query.queryKey === 'string' &&
              query.queryKey.startsWith(prefix),
          })
        );
        return;
      }
      queryClient.invalidateQueries();
    },
    onError: (error) => {
      console.error(error);
    },
  });
};
